import React, { Suspense } from 'react';
import { css } from '@emotion/react';

import { UploadStage } from '$/GraphQL/schema/types';
import { UploadMode } from '$/sections/Home/HomePage/UploadVideo/types';
import { stageToString } from '$/sections/Home/WebinarEditor/UploadProgress/stageToString';
import { Loader } from 'semantic-ui-react';
import { useWebinarPlayerContext } from '../context';
import { PlayButtonSizePx } from '../Controls/PlayButtonSizePx';
import { OverVideoErrorBox } from '../PlayerError';
import { PlayerMode } from '../types';
import { RoomPartsBase } from './RoomPartsBase';

const UploadVideoButton = React.lazy(() => import('../UploadVideoButton'));

const VimeoPlayer: React.FC = () => {
  const { playerIframeRef, vimeoVideoId, vimeoHash, mode, isReplayExpired } =
    useWebinarPlayerContext();
  const iFrameID = React.useRef(new Date().getTime());

  if (isReplayExpired) return null;

  return (
    <>
      <iframe
        id={`VimeoIFrame-${
          /* Must be unique so we can easily detect a new instance of IFrame */
          iFrameID.current
        }`}
        frameBorder='0'
        allow='autoplay; encrypted-media; fullscreen;'
        ref={playerIframeRef}
        css={css`
          background: transparent;
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          overflow: hidden;
        `}
        src={`https://player.vimeo.com/video/${vimeoVideoId}?h=${vimeoHash}&controls=0&quality=auto&responsive=1&title=0&portrait=0&byline=0&loop=0&autopause=0&${
          // DD: When we are in edit or replay mode autoplay=1 so that seeking shows latest frame
          // This should work well becuase there's a PLAY button that has to be pressed to play the video anyway
          mode === PlayerMode.live ? 'autoplay=0' : 'autoplay=1'
        }&${
          // Issue: the video will make a sound for about 1 second when loading
          // Using `muted=1` for edit mode to prevent the video makes a sound when loading
          mode === PlayerMode.edit ? 'muted=1' : 'muted=0'
        }`}
      />
    </>
  );
};

export const PresentationRoomStage: React.FC = () => {
  const { mode, ewebinar, error, errorCode, isBuffering, isSeeking, showOverlay } = useWebinarPlayerContext();
  const stage = ewebinar?.uploadStatus?.stage;

  const UploadVideoButtonOrProgress = (): JSX.Element => {
    return ewebinar?.uploadStatus?.done === false ? (
      <div className='flex flex-col w-full h-full items-center justify-center text-black text-lg'>
        <div className='text-3xl mb-4'>{`${ewebinar?.uploadStatus?.progress ?? ''}%`}</div>
        {`${stageToString(stage || UploadStage.Uploading)} your video.`}
      </div>
    ) : (
      <div
        className='absolute flex flex-col w-full h-full items-center justify-center text-white text-lg'
        css={css`
          pointer-events: auto;
          > div {
            transform: scale(2.75);
          }
        `}
      >
        <Suspense>
          <UploadVideoButton ewebinarId={ewebinar!.id} mode={UploadMode.New} />
        </Suspense>
      </div>
    );
  };

  return (
    <>
      {(isBuffering || isSeeking) && !showOverlay && (
        <div
          className='z-player-control scale-control control flex items-center justify-center'
          css={css`
            position: fixed;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            pointer-events: none;
          `}
        >
          <Loader
            active
            size='massive'
            css={css`
              font-size: 40px !important;
              pointer-events: none;
              width: ${PlayButtonSizePx + 14}px !important;
              height: ${PlayButtonSizePx + 14}px !important;
              &:after,
              &:before {
                left: 32px !important;
                width: ${PlayButtonSizePx + 14}px !important;
                height: ${PlayButtonSizePx + 14}px !important;
              }
              &:before {
                border-color: #ffffff80 !important;
              }
            `}
          />
        </div>
      )}

      <OverVideoErrorBox error={error} errorCode={errorCode} mode={mode} />

      {ewebinar?.vimeoVideoId ? (
        <VimeoPlayer />
      ) : (
        <div
          className='control'
          css={css`
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
          `}
        >
          <UploadVideoButtonOrProgress />
        </div>
      )}
    </>
  );
};

export class PresentationRoomParts extends RoomPartsBase {
  /*
  public stageComponent(): { component: React.FC; zIndex?: number } | null {
    No need for this as any room that requires video needs to share the same video player
    return PresentationRoomStage;
  }
  */
}
